import { DEFAULT_THEME } from '../theming/default-theme';
import { isColorToken, isFontToken } from '../theming/tokens';

import type {
  ElementPosition,
  ElementPositionOverride,
  ImageElement,
  InteractiveWidgetType,
  PlaceholderId,
  ResolvedTheme,
  ShapeCorner,
  Slide,
  SlideContentV1,
  SlideElement,
  SlideTemplate,
  SlideTextAlign,
  SlideTextStyle,
  SlideVerticalAlign,
} from './slide';
import { MIN_ELEMENT_SIZE, SHAPE_CORNERS, SLIDE_PAGE_HEIGHT, SLIDE_PAGE_WIDTH } from './slide';
import type { RubricContent } from './rubric';

export type ResourceKind = 'link' | 'document' | 'slide' | 'file' | 'rubric';
export type ResourceParentType = 'course' | 'unit' | 'lesson';

export interface ResourceParentLocator {
  parentCourseId?: string | null;
  parentUnitId?: string | null;
  parentLessonId?: string | null;
  courseId?: string;
}

export interface TiptapDocNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapDocNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}

export interface DocumentContentV1 {
  schemaVersion: '1';
  title: string;
  tiptapDoc: TiptapDocNode;
  editorSettings: {
    pageSize: 'letter' | 'a4';
    margins: 'normal' | 'narrow' | 'wide';
  };
}

export interface LinkResourceContent {
  url: string;
}

export interface ResourceBase {
  id: string;
  resourceKind: ResourceKind;
  title: string;
  description: string | null;
  content: unknown;
  version: number;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  parentCourseId: string | null;
  parentUnitId: string | null;
  parentLessonId: string | null;
  courseId: string;
  position: number;
}

export interface LinkResource extends ResourceBase {
  resourceKind: 'link';
  content: LinkResourceContent;
}

export interface DocumentResource extends ResourceBase {
  resourceKind: 'document';
  content: DocumentContentV1;
}

export interface SlideResource extends ResourceBase {
  resourceKind: 'slide';
  content: import('./slide').SlideContentV1;
}

export interface FileResourceContent {
  storageBucket: 'uploaded-files';
  storagePath: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
}

export interface FileResource extends ResourceBase {
  resourceKind: 'file';
  content: FileResourceContent;
}

export interface RubricResourceContent {
  rubric: RubricContent;
  /** id of the library rubric this was copied from. Display-only — no FK, no sync. */
  sourceRubricId?: string;
}

export interface RubricResource extends ResourceBase {
  resourceKind: 'rubric';
  content: RubricResourceContent;
}

export type Resource =
  | ResourceBase
  | LinkResource
  | DocumentResource
  | SlideResource
  | FileResource
  | RubricResource;

export interface CreateResourceRequest {
  resourceKind: ResourceKind;
  title: string;
  description?: string | null;
  content: unknown;
}

export interface UpdateResourceRequest {
  id: string;
  title?: string;
  description?: string | null;
  content?: unknown;
  version?: number;
}

export type ResourceLibraryCategory =
  | 'worksheets'
  | 'graphic_organizers'
  | 'exit_tickets'
  | 'vocabulary'
  | 'general';

export interface ResourceTemplate {
  id: string;
  templateKind: 'document';
  title: string;
  description: string | null;
  categories: string[];
  gradeBands: string[];
  duration: string | null;
  difficulty: string | null;
  tags: string[];
  preview: Record<string, unknown> | null;
  baseContent: DocumentContentV1 | Record<string, unknown>;
  meta: Record<string, unknown> | null;
  agentInstructions: string | null;
  generatorProfile: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateFromTemplateRequest {
  templateId: string;
  parentCourseId?: string | null;
  parentUnitId?: string | null;
  parentLessonId?: string | null;
  courseId?: string;
  userContext: string;
}

export interface GenerateFromTemplateResult {
  resourceId: string;
  resourceKind: 'document' | 'slide';
}

const CANONICAL_SLIDE_FONT_FAMILIES = [
  'Arial, sans-serif',
  'Helvetica, sans-serif',
  'Times New Roman, serif',
  'Georgia, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
  'Impact, sans-serif',
  'Comic Sans MS, cursive',
  'Palatino Linotype, serif',
] as const;

const SLIDE_FONT_FAMILY_BY_PRIMARY_NAME = new Map(
  CANONICAL_SLIDE_FONT_FAMILIES.map((fontFamily) => [
    fontFamily
      .split(',')[0]
      .trim()
      .replace(/^['"]|['"]$/g, '')
      .toLowerCase(),
    fontFamily,
  ])
);

function parsePlaceholderSearchTerm(value: string): string | null {
  const match = value.trim().match(/^PLACEHOLDER\s*:\s*(.+)$/i);
  if (!match) return null;
  const term = match[1].trim();
  return term.length > 0 ? term : null;
}

function isPersistedImageSrc(value: string): boolean {
  return /^(https?:|data:|blob:|file:)/i.test(value);
}

function parseImageQueryFromSrc(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const placeholder = parsePlaceholderSearchTerm(trimmed);
  if (placeholder) return placeholder;
  if (isPersistedImageSrc(trimmed)) return null;
  if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) return null;
  if (/[\\/]/.test(trimmed)) return null;
  if (/\.[a-z0-9]{2,8}($|[?#])/i.test(trimmed)) return null;
  if (trimmed.length <= 120) return trimmed;
  return null;
}

function normalizeTiptapImageNodes<T extends TiptapDocNode>(node: T): T {
  const next: TiptapDocNode = {
    ...node,
    attrs: node.attrs ? { ...node.attrs } : undefined,
  };

  if (next.type === 'image' || next.type === 'imagePlaceholder') {
    const attrs = next.attrs ? { ...next.attrs } : {};
    const id = typeof attrs.id === 'string' ? attrs.id.trim() : '';
    const src = typeof attrs.src === 'string' ? attrs.src.trim() : '';
    const imageQuery = typeof attrs.imageQuery === 'string' ? attrs.imageQuery.trim() : '';
    const storagePath = typeof attrs.storagePath === 'string' ? attrs.storagePath.trim() : '';
    const inferredQuery = imageQuery || parseImageQueryFromSrc(src);

    attrs.id = id || crypto.randomUUID();

    if (storagePath) {
      // Storage-backed image — keep as image regardless of src (signed URL is resolved at render time)
      delete attrs.src;
      delete attrs.imageQuery;
      next.type = 'image';
      next.attrs = attrs;
    } else if (src && isPersistedImageSrc(src)) {
      attrs.src = src;
      delete attrs.imageQuery;
      next.type = 'image';
      next.attrs = attrs;
    } else if (inferredQuery) {
      attrs.imageQuery = inferredQuery;
      delete attrs.src;
      next.type = 'imagePlaceholder';
      next.attrs = attrs;
    } else if (src) {
      attrs.src = src;
      delete attrs.imageQuery;
      next.type = 'image';
      next.attrs = attrs;
    } else {
      delete attrs.src;
      delete attrs.imageQuery;
      next.type = 'imagePlaceholder';
      next.attrs = Object.keys(attrs).length > 0 ? attrs : undefined;
    }
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.map((child) => normalizeTiptapImageNodes(child));
  }

  return next as T;
}

export function stripTransientStorageImageUrlsFromTiptapDoc<T extends TiptapDocNode>(doc: T): T {
  return normalizeTiptapImageNodes(doc);
}

export function removeStorageBackedImagesForDuplicate<T extends TiptapDocNode>(doc: T): T {
  const next: TiptapDocNode = {
    ...doc,
    attrs: doc.attrs ? { ...doc.attrs } : undefined,
  };

  if ((next.type === 'image' || next.type === 'imagePlaceholder') && next.attrs) {
    const hasStorageReference = Boolean(
      normalizeNonEmptyString(next.attrs.storagePath) ||
      normalizeNonEmptyString(next.attrs.storageBucket)
    );

    if (hasStorageReference) {
      const imageQuery =
        normalizeNonEmptyString(next.attrs.imageQuery) ||
        normalizeNonEmptyString(next.attrs.alt) ||
        normalizeNonEmptyString(next.attrs.title) ||
        'image';

      delete next.attrs.src;
      delete next.attrs.storageBucket;
      delete next.attrs.storagePath;
      next.attrs.imageQuery = imageQuery;
    }
  }

  if (Array.isArray(next.content)) {
    next.content = next.content.map((child) => removeStorageBackedImagesForDuplicate(child));
  }

  return normalizeTiptapImageNodes(next as T);
}

export function stripStorageImagePathsFromDocumentContent(
  content: DocumentContentV1
): DocumentContentV1 {
  if (!isTiptapDoc(content.tiptapDoc)) return content;

  return {
    ...content,
    tiptapDoc: removeStorageBackedImagesForDuplicate(content.tiptapDoc),
  };
}

export interface GoogleDriveImportResultItem {
  fileId: string;
  resourceId: string;
  title: string;
  reused: boolean;
  url: string;
}

export interface GoogleDriveImportResult {
  count: number;
  items: GoogleDriveImportResultItem[];
}

export interface RefreshCourseLinkContentResult {
  refreshed: number;
  skipped: number;
  errors: string[];
}

export interface GoogleDocsExportResult {
  documentId: string;
  url: string;
}

export interface GoogleNativeImportWarning {
  code: string;
  message: string;
  path?: string;
}

export interface GoogleNativeImportResultItem {
  fileId: string;
  resourceId?: string;
  resourceKind?: string;
  title?: string;
  url?: string;
  imported: boolean;
  error?: string;
  warnings?: GoogleNativeImportWarning[];
}

export interface GoogleNativeImportResult {
  count: number;
  items: GoogleNativeImportResultItem[];
}

export function asLinkUrl(resource: Resource): string | null {
  if (resource.resourceKind !== 'link') return null;
  const content = resource.content as LinkResourceContent;
  return typeof content?.url === 'string' ? content.url : null;
}

export function normalizeExternalResourceUrl(rawUrl: string | null | undefined): string | null {
  const url = rawUrl?.trim();
  if (!url) return null;

  if (/^([a-z][a-z\d+\-.]*:\/\/|mailto:|tel:)/i.test(url)) {
    return url;
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  return `https://${url}`;
}

export function asDocumentContent(resource: Resource): DocumentContentV1 | null {
  if (resource.resourceKind !== 'document') return null;

  const fallback = createEmptyDocumentContent(resource.title || 'Untitled Document');
  const raw = resource.content;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return fallback;
  }

  const maybe = raw as Partial<DocumentContentV1> & Record<string, unknown>;
  const tiptapDoc = isTiptapDoc(maybe.tiptapDoc)
    ? normalizeTiptapImageNodes(maybe.tiptapDoc)
    : fallback.tiptapDoc;
  const pageSize = maybe.editorSettings?.pageSize === 'a4' ? 'a4' : 'letter';
  const margins =
    maybe.editorSettings?.margins === 'narrow'
      ? 'narrow'
      : maybe.editorSettings?.margins === 'wide'
        ? 'wide'
        : 'normal';

  return {
    schemaVersion: '1',
    title:
      typeof maybe.title === 'string' && maybe.title.trim().length > 0
        ? maybe.title
        : resource.title || fallback.title,
    tiptapDoc,
    editorSettings: { pageSize, margins },
  };
}

export function createEmptyDocumentContent(title = 'Untitled Document'): DocumentContentV1 {
  return {
    schemaVersion: '1',
    title,
    tiptapDoc: {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
        },
      ],
    },
    editorSettings: {
      pageSize: 'letter',
      margins: 'normal',
    },
  };
}

const VALID_SLIDE_TEMPLATES = new Set<SlideTemplate>([
  'BLANK',
  'TITLE',
  'TITLE_AND_BODY',
  'TITLE_AND_TWO_COLUMNS',
]);
const VALID_PLACEHOLDER_IDS = new Set<PlaceholderId>([
  'TITLE',
  'SUBTITLE',
  'BODY',
  'LEFT_BODY',
  'RIGHT_BODY',
]);
const VALID_TEXT_ALIGNMENTS = new Set<SlideTextAlign>(['left', 'center', 'right', 'justify']);
const VALID_VERTICAL_ALIGNS = new Set<SlideVerticalAlign>(['top', 'middle', 'bottom']);
const VALID_BORDER_STYLES = new Set(['solid', 'dashed', 'dotted', 'none']);
const VALID_SHAPE_TYPES = new Set(['rectangle', 'circle', 'line']);
const VALID_IMAGE_OBJECT_FITS = new Set(['fill', 'cover', 'contain']);
const VALID_WIDGET_TYPES = new Set<InteractiveWidgetType>(['calculator', 'timer']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeRotation(value: number): number {
  return ((value % 360) + 360) % 360;
}

function normalizeNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeCssString(value: unknown): string | undefined {
  const trimmed = normalizeNonEmptyString(value);
  if (trimmed === undefined) return undefined;
  // A $-prefixed value that isn't a valid color token is malformed — drop it to
  // the field default (§7.3) so a junk token never persists or warns at render.
  // Valid tokens and raw values (hex / named / rgb) pass through unchanged.
  if (trimmed.startsWith('$') && !isColorToken(trimmed)) return undefined;
  return trimmed;
}

function normalizeSlideFontFamily(value: unknown): string | undefined {
  const fontFamily = normalizeNonEmptyString(value);
  if (!fontFamily) return undefined;
  // Valid font token passes through; a $-prefixed non-token is malformed (§7.3).
  if (fontFamily.startsWith('$')) return isFontToken(fontFamily) ? fontFamily : undefined;

  const primaryFont = fontFamily
    .split(',')[0]
    ?.trim()
    .replace(/^['"]|['"]$/g, '')
    .toLowerCase();

  return (primaryFont && SLIDE_FONT_FAMILY_BY_PRIMARY_NAME.get(primaryFont)) ?? fontFamily;
}

function normalizeFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Clamp a crop edge fraction to [0, 1); non-numbers become 0. */
function normalizeCropEdge(value: unknown): number {
  const n = normalizeFiniteNumber(value);
  if (n === undefined) return 0;
  return Math.min(Math.max(n, 0), 0.999);
}

/**
 * Normalize an image crop window. Returns undefined when absent or when the
 * crop is a no-op (nothing trimmed) or degenerate (an axis fully trimmed away).
 */
function normalizeImageCrop(value: unknown): ImageElement['content']['crop'] | undefined {
  if (!isRecord(value)) return undefined;
  const left = normalizeCropEdge(value.left);
  const right = normalizeCropEdge(value.right);
  const top = normalizeCropEdge(value.top);
  const bottom = normalizeCropEdge(value.bottom);
  const trims = left + right + top + bottom > 0.001;
  const validAxes = left + right < 0.999 && top + bottom < 0.999;
  if (!trims || !validAxes) return undefined;
  return { left, right, top, bottom };
}

/**
 * Normalize the set of corners a shape's borderRadius applies to. Returns a
 * canonical (deduped, ordered) subset, or undefined when the value is absent,
 * malformed, empty, or covers all four corners (all-four is the default).
 */
function normalizeRoundedCorners(value: unknown): ShapeCorner[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const present = new Set(
    value.filter((c): c is ShapeCorner => SHAPE_CORNERS.includes(c as ShapeCorner))
  );
  if (present.size === 0 || present.size === SHAPE_CORNERS.length) return undefined;
  return SHAPE_CORNERS.filter((c) => present.has(c));
}

function normalizePositiveNumber(value: unknown): number | undefined {
  const next = normalizeFiniteNumber(value);
  return next !== undefined && next > 0 ? next : undefined;
}

function normalizeNonNegativeNumber(value: unknown): number | undefined {
  const next = normalizeFiniteNumber(value);
  return next !== undefined ? Math.max(0, next) : undefined;
}

function normalizeSlideTitle(value: unknown, fallback: string): string {
  const trimmed = normalizeNonEmptyString(value);
  return trimmed ?? fallback;
}

function normalizeSlideId(value: unknown): string {
  return normalizeNonEmptyString(value) ?? crypto.randomUUID();
}

function createFallbackSlide(): Slide {
  // Bare placeholder-bound elements — geometry/style come from the layout at
  // render time (§8.3). No background ⇒ inherits the layout background.
  return {
    id: crypto.randomUUID(),
    template: 'TITLE',
    elements: [
      { id: crypto.randomUUID(), type: 'text', placeholderId: 'TITLE', content: { text: '' } },
      { id: crypto.randomUUID(), type: 'text', placeholderId: 'SUBTITLE', content: { text: '' } },
    ],
  };
}

function normalizeElementPosition(value: unknown, index: number): ElementPosition | null {
  if (!isRecord(value)) return null;
  const x = typeof value.x === 'number' && Number.isFinite(value.x) ? value.x : null;
  const y = typeof value.y === 'number' && Number.isFinite(value.y) ? value.y : null;
  const width =
    typeof value.width === 'number' && Number.isFinite(value.width)
      ? Math.max(MIN_ELEMENT_SIZE, value.width)
      : null;
  const height =
    typeof value.height === 'number' && Number.isFinite(value.height)
      ? Math.max(MIN_ELEMENT_SIZE, value.height)
      : null;

  if (x === null || y === null || width === null || height === null) return null;

  const rotation =
    typeof value.rotation === 'number' && Number.isFinite(value.rotation)
      ? normalizeRotation(value.rotation)
      : 0;
  const rawZIndex =
    typeof value.zIndex === 'number' && Number.isFinite(value.zIndex)
      ? Math.round(value.zIndex)
      : index + 1;

  return {
    x,
    y,
    width,
    height,
    rotation,
    ...(value.flipH === true ? { flipH: true } : {}),
    ...(value.flipV === true ? { flipV: true } : {}),
    zIndex: Math.max(1, rawZIndex),
  };
}

/** Keep whatever valid geometry fields are present; omit the rest. Used for
 *  placeholder-bound elements whose stored position may be partial (e.g. a
 *  z-order edit stores only `{ zIndex }`) or absent (inherited at render). */
function normalizePositionOverride(value: unknown): ElementPositionOverride | undefined {
  if (!isRecord(value)) return undefined;
  const out: ElementPositionOverride = {};
  if (typeof value.x === 'number' && Number.isFinite(value.x)) out.x = value.x;
  if (typeof value.y === 'number' && Number.isFinite(value.y)) out.y = value.y;
  if (typeof value.width === 'number' && Number.isFinite(value.width))
    out.width = Math.max(MIN_ELEMENT_SIZE, value.width);
  if (typeof value.height === 'number' && Number.isFinite(value.height))
    out.height = Math.max(MIN_ELEMENT_SIZE, value.height);
  if (typeof value.rotation === 'number' && Number.isFinite(value.rotation))
    out.rotation = normalizeRotation(value.rotation);
  if (value.flipH === true) out.flipH = true;
  if (value.flipV === true) out.flipV = true;
  if (typeof value.zIndex === 'number' && Number.isFinite(value.zIndex))
    out.zIndex = Math.max(1, Math.round(value.zIndex));
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizePlaceholderId(value: unknown): PlaceholderId | undefined {
  const candidate = normalizeNonEmptyString(value);
  // Safe: membership in VALID_PLACEHOLDER_IDS is the runtime guarantee.
  return candidate && VALID_PLACEHOLDER_IDS.has(candidate as PlaceholderId)
    ? (candidate as PlaceholderId)
    : undefined;
}

function normalizeStoredTheme(value: unknown): ResolvedTheme | undefined {
  if (!isRecord(value)) return undefined;
  if (!isRecord(value.palette) || !isRecord(value.fonts) || !isRecord(value.layouts)) {
    return undefined;
  }
  // Trust the stored theme shape (written by createEmptySlideContent). Phase 1
  // ships a single theme, so deep validation is unnecessary; the structural
  // guard above is the runtime guarantee for this assertion.
  return value as unknown as ResolvedTheme;
}

function normalizeSlideTextStyle(value: unknown): SlideTextStyle {
  if (!isRecord(value)) return {};

  const fontFamily = normalizeSlideFontFamily(value.fontFamily);
  const fontWeight = normalizeNonEmptyString(value.fontWeight);
  const fontStyle = normalizeNonEmptyString(value.fontStyle);
  const textDecoration = normalizeNonEmptyString(value.textDecoration);
  const textAlign = normalizeNonEmptyString(value.textAlign);
  const verticalAlign = normalizeNonEmptyString(value.verticalAlign);
  const borderStyle = normalizeNonEmptyString(value.borderStyle);
  const color = normalizeCssString(value.color);
  const backgroundColor = normalizeCssString(value.backgroundColor);
  const borderColor = normalizeCssString(value.borderColor);
  const fontSize = normalizePositiveNumber(value.fontSize);
  const lineHeight = normalizePositiveNumber(value.lineHeight);
  const borderWidth = normalizeNonNegativeNumber(value.borderWidth);
  const borderRadius = normalizeNonNegativeNumber(value.borderRadius);
  const padding = normalizeNonNegativeNumber(value.padding);

  return {
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontSize !== undefined ? { fontSize } : {}),
    ...(fontWeight ? { fontWeight } : {}),
    ...(fontStyle ? { fontStyle } : {}),
    ...(textDecoration ? { textDecoration } : {}),
    ...(color ? { color } : {}),
    ...(lineHeight !== undefined ? { lineHeight } : {}),
    ...(textAlign && VALID_TEXT_ALIGNMENTS.has(textAlign as SlideTextAlign)
      ? { textAlign: textAlign as SlideTextAlign }
      : {}),
    ...(verticalAlign && VALID_VERTICAL_ALIGNS.has(verticalAlign as SlideVerticalAlign)
      ? { verticalAlign: verticalAlign as SlideVerticalAlign }
      : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderColor ? { borderColor } : {}),
    ...(borderWidth !== undefined ? { borderWidth } : {}),
    ...(borderStyle && VALID_BORDER_STYLES.has(borderStyle)
      ? { borderStyle: borderStyle as 'solid' | 'dashed' | 'dotted' | 'none' }
      : {}),
    ...(borderRadius !== undefined ? { borderRadius } : {}),
    ...(padding !== undefined ? { padding } : {}),
  };
}

function normalizeSlideElement(value: unknown, index: number): SlideElement | null {
  if (!isRecord(value)) return null;
  const type = normalizeNonEmptyString(value.type);
  if (!type) return null;

  // A `placeholderId`-bound text element may omit position/style (inherited from
  // the layout at render). A free element (no placeholderId) must carry its own
  // full geometry — otherwise it is dropped, as before.
  const placeholderId = type === 'text' ? normalizePlaceholderId(value.placeholderId) : undefined;
  let position: ElementPositionOverride | undefined;
  if (placeholderId) {
    position = normalizePositionOverride(value.position);
  } else {
    const full = normalizeElementPosition(value.position, index);
    if (!full) return null;
    position = full;
  }
  const positionField = position ? { position } : {};
  const lockedField = typeof value.locked === 'boolean' ? { locked: value.locked } : {};

  if (type === 'text') {
    const content = isRecord(value.content) ? value.content : {};
    const style = normalizeSlideTextStyle(value.style);
    return {
      id: normalizeSlideId(value.id),
      type: 'text',
      ...(placeholderId ? { placeholderId } : {}),
      ...positionField,
      ...lockedField,
      content: {
        text: typeof content.text === 'string' ? content.text : '',
        ...(isRecord(content.tiptapDoc) ? { tiptapDoc: content.tiptapDoc } : {}),
      },
      ...(Object.keys(style).length > 0 ? { style } : {}),
    };
  }

  if (type === 'shape') {
    const content = isRecord(value.content) ? value.content : {};
    const shapeType = normalizeNonEmptyString(content.shapeType);
    const fillColor = normalizeCssString(content.fillColor);
    const strokeColor = normalizeCssString(content.strokeColor);
    const strokeWidth = normalizeNonNegativeNumber(content.strokeWidth);
    const borderRadius = normalizeNonNegativeNumber(content.borderRadius);
    // Corner set only matters when there is a radius to apply.
    const roundedCorners =
      borderRadius !== undefined ? normalizeRoundedCorners(content.roundedCorners) : undefined;
    return {
      id: normalizeSlideId(value.id),
      type: 'shape',
      ...positionField,
      ...lockedField,
      content: {
        shapeType:
          shapeType && VALID_SHAPE_TYPES.has(shapeType)
            ? (shapeType as 'rectangle' | 'circle' | 'line')
            : 'rectangle',
        ...(fillColor ? { fillColor } : {}),
        ...(strokeColor ? { strokeColor } : {}),
        ...(strokeWidth !== undefined ? { strokeWidth } : {}),
        ...(borderRadius !== undefined ? { borderRadius } : {}),
        ...(roundedCorners ? { roundedCorners } : {}),
      },
    };
  }

  if (type === 'image') {
    const content = isRecord(value.content) ? value.content : {};
    const objectFit = normalizeNonEmptyString(content.objectFit);
    const crop = normalizeImageCrop(content.crop);
    return {
      id: normalizeSlideId(value.id),
      type: 'image',
      ...positionField,
      ...lockedField,
      content: {
        ...(normalizeNonEmptyString(content.src)
          ? { src: normalizeNonEmptyString(content.src) }
          : {}),
        ...(normalizeNonEmptyString(content.imageQuery)
          ? { imageQuery: normalizeNonEmptyString(content.imageQuery) }
          : {}),
        ...(normalizeNonEmptyString(content.alt)
          ? { alt: normalizeNonEmptyString(content.alt) }
          : {}),
        ...(normalizeNonEmptyString(content.storageBucket)
          ? { storageBucket: normalizeNonEmptyString(content.storageBucket) }
          : {}),
        ...(normalizeNonEmptyString(content.storagePath)
          ? { storagePath: normalizeNonEmptyString(content.storagePath) }
          : {}),
        ...(objectFit && VALID_IMAGE_OBJECT_FITS.has(objectFit)
          ? { objectFit: objectFit as 'fill' | 'cover' | 'contain' }
          : {}),
        ...(crop ? { crop } : {}),
      },
    };
  }

  if (type === 'interactive') {
    const content = isRecord(value.content) ? value.content : {};
    const widgetType = normalizeNonEmptyString(content.widgetType);
    if (!widgetType || !VALID_WIDGET_TYPES.has(widgetType as InteractiveWidgetType)) return null;
    return {
      id: normalizeSlideId(value.id),
      type: 'interactive',
      ...positionField,
      ...lockedField,
      content: {
        widgetType: widgetType as InteractiveWidgetType,
        config: isRecord(content.config) ? content.config : {},
      },
    };
  }

  if (type === 'video') {
    const content = isRecord(value.content) ? value.content : {};
    const videoId = normalizeNonEmptyString(content.videoId);
    const title = normalizeNonEmptyString(content.title);
    const channelName = normalizeNonEmptyString(content.channelName);
    const thumbnailUrl = normalizeNonEmptyString(content.thumbnailUrl);
    const startSeconds = normalizeNonNegativeNumber(content.startSeconds);
    const endSeconds = normalizeNonNegativeNumber(content.endSeconds);
    const alt = normalizeNonEmptyString(content.alt);
    return {
      id: normalizeSlideId(value.id),
      type: 'video',
      ...positionField,
      ...lockedField,
      content: {
        // Single source supported today. Future sources land as additional
        // discriminator values without changing this normalizer's shape.
        source: 'youtube',
        ...(videoId ? { videoId } : {}),
        ...(title ? { title } : {}),
        ...(channelName ? { channelName } : {}),
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(startSeconds !== undefined ? { startSeconds } : {}),
        ...(endSeconds !== undefined ? { endSeconds } : {}),
        ...(typeof content.autoplay === 'boolean' ? { autoplay: content.autoplay } : {}),
        ...(typeof content.mute === 'boolean' ? { mute: content.mute } : {}),
        ...(typeof content.forceCaptions === 'boolean'
          ? { forceCaptions: content.forceCaptions }
          : {}),
        ...(alt ? { alt } : {}),
      },
    };
  }

  return null;
}

function normalizeSlide(value: unknown): Slide | null {
  if (!isRecord(value)) return null;
  const elements = Array.isArray(value.elements)
    ? value.elements
        .map((element, index) => normalizeSlideElement(element, index))
        .filter((element): element is SlideElement => element !== null)
    : [];
  const template = normalizeNonEmptyString(value.template);
  const background = isRecord(value.background) ? value.background : null;
  const backgroundColor = normalizeCssString(background?.color);

  return {
    id: normalizeSlideId(value.id),
    template:
      template && VALID_SLIDE_TEMPLATES.has(template as SlideTemplate)
        ? (template as SlideTemplate)
        : 'BLANK',
    elements,
    // Absent background inherits the layout background (§6.2) — do not pin white.
    ...(backgroundColor ? { background: { color: backgroundColor } } : {}),
  };
}

export function createEmptySlideContent(title = 'Untitled Presentation'): SlideContentV1 {
  const presentationTitle = normalizeSlideTitle(title, 'Untitled Presentation');
  return {
    schemaVersion: '1',
    presentation: {
      id: crypto.randomUUID(),
      title: presentationTitle,
      // New decks embed the default theme (§9). Clone so later edits to one deck
      // never mutate the shared constant.
      theme: structuredClone(DEFAULT_THEME),
      slides: [createFallbackSlide()],
      pageSize: { width: SLIDE_PAGE_WIDTH, height: SLIDE_PAGE_HEIGHT },
    },
  };
}

export function asSlideContent(resource: Resource): SlideContentV1 | null {
  if (resource.resourceKind !== 'slide') return null;

  const fallback = createEmptySlideContent(resource.title || 'Untitled Presentation');
  const raw = resource.content;
  if (!isRecord(raw)) return fallback;

  const presentation = isRecord(raw.presentation) ? raw.presentation : null;
  if (!presentation) return fallback;

  const normalizedSlides = Array.isArray(presentation.slides)
    ? presentation.slides
        .map((slide) => normalizeSlide(slide))
        .filter((slide): slide is Slide => slide !== null)
    : [];

  // Preserve an embedded theme through the reconstruction (the object literal
  // below would otherwise drop it). Legacy/un-themed decks keep `theme` absent
  // and render via the resolver's DEFAULT_THEME fallback.
  const theme = normalizeStoredTheme(presentation.theme);

  return {
    schemaVersion: '1',
    presentation: {
      id: normalizeSlideId(presentation.id),
      title: normalizeSlideTitle(presentation.title, resource.title || fallback.presentation.title),
      ...(theme ? { theme } : {}),
      slides: normalizedSlides.length > 0 ? normalizedSlides : fallback.presentation.slides,
      pageSize: {
        width: SLIDE_PAGE_WIDTH,
        height: SLIDE_PAGE_HEIGHT,
      },
    },
  };
}

export function stripStorageImagePathsFromSlideContent(content: SlideContentV1): SlideContentV1 {
  return {
    ...content,
    presentation: {
      ...content.presentation,
      slides: content.presentation.slides.map((slide) => ({
        ...slide,
        elements: (slide.elements ?? []).map((element) => {
          if (element.type !== 'image') return element;
          if (!element.content.storagePath && !element.content.storageBucket) return element;

          const restContent = { ...element.content };
          delete restContent.storageBucket;
          delete restContent.storagePath;
          delete restContent.src;

          return {
            ...element,
            content: {
              ...restContent,
              imageQuery: restContent.imageQuery || restContent.alt || 'image',
            },
          };
        }),
      })),
    },
  };
}

export function asFileContent(resource: Resource): FileResourceContent | null {
  if (resource.resourceKind !== 'file') return null;
  const raw = resource.content;
  if (!isRecord(raw)) return null;
  if (typeof raw.storagePath !== 'string' || !raw.storagePath) return null;
  return {
    storageBucket: 'uploaded-files',
    storagePath: raw.storagePath as string,
    originalFilename: typeof raw.originalFilename === 'string' ? raw.originalFilename : 'unknown',
    mimeType: typeof raw.mimeType === 'string' ? raw.mimeType : 'application/octet-stream',
    fileSize: typeof raw.fileSize === 'number' ? raw.fileSize : 0,
  };
}

export function asRubricContent(resource: Resource): RubricResourceContent | null {
  if (resource.resourceKind !== 'rubric') return null;
  const raw = resource.content;
  if (!isRecord(raw)) return null;
  const rubric = raw.rubric;
  if (!isRecord(rubric)) return null;
  const rubricType = rubric.rubricType;
  if (rubricType !== 'analytic' && rubricType !== 'holistic' && rubricType !== 'single-point')
    return null;
  if (rubric.schemaVersion !== '1') return null;
  if (rubricType === 'analytic') {
    if (
      !Array.isArray(rubric.performanceLevels) ||
      !Array.isArray(rubric.criteria) ||
      !Array.isArray(rubric.cells)
    )
      return null;
  } else if (rubricType === 'holistic') {
    if (!Array.isArray(rubric.performanceLevels)) return null;
  } else if (rubricType === 'single-point') {
    if (!Array.isArray(rubric.criteria)) return null;
  }
  const sourceRubricId = typeof raw.sourceRubricId === 'string' ? raw.sourceRubricId : undefined;
  return {
    rubric: rubric as unknown as RubricContent,
    sourceRubricId,
  };
}

function isTiptapDoc(value: unknown): value is TiptapDocNode {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const maybe = value as Record<string, unknown>;
  return typeof maybe.type === 'string';
}

/**
 * Node types that an LLM may accidentally place inside a `marks` array.
 * Lists, headings, etc. are ProseMirror *nodes*, not marks.
 */
const NODE_TYPES_MISUSED_AS_MARKS = new Set([
  'blockquote',
  'bulletList',
  'orderedList',
  'taskList',
  'taskItem',
  'listItem',
  'heading',
  'codeBlock',
  'horizontalRule',
  'image',
  'imagePlaceholder',
  'table',
  'tableRowGroup',
  'tableRow',
  'tableCell',
  'tableHeader',
  'paragraph',
  'doc',
]);

const DISALLOWED_MARK_TYPES = new Set(['code']);
const DISALLOWED_NODE_TYPES = new Set(['blockquote', 'codeBlock']);

function sanitizeTiptapNodes(nodes: TiptapDocNode[]): TiptapDocNode[] {
  return nodes.flatMap((node) => sanitizeSingleTiptapNode(node));
}

function sanitizeSingleTiptapNode(node: TiptapDocNode): TiptapDocNode[] {
  if (!node || typeof node !== 'object') return [];

  const cleaned: TiptapDocNode = { ...node };

  if (Array.isArray(cleaned.marks)) {
    cleaned.marks = cleaned.marks.filter(
      (mark) => !NODE_TYPES_MISUSED_AS_MARKS.has(mark.type) && !DISALLOWED_MARK_TYPES.has(mark.type)
    );
    if (cleaned.marks.length === 0) delete cleaned.marks;
  }

  if (Array.isArray(cleaned.content)) {
    cleaned.content = sanitizeTiptapNodes(cleaned.content);
  }

  if (cleaned.type === 'tableRowGroup') {
    return cleaned.content ?? [];
  }

  if (cleaned.type === 'blockquote') {
    return cleaned.content ?? [];
  }

  if (cleaned.type === 'codeBlock') {
    if (!cleaned.content || cleaned.content.length === 0) {
      return [];
    }

    return [
      {
        type: 'paragraph',
        content: cleaned.content,
      },
    ];
  }

  if (DISALLOWED_NODE_TYPES.has(cleaned.type)) {
    return cleaned.content ?? [];
  }

  return [cleaned];
}

/**
 * Recursively walk a tiptap doc JSON tree and strip any marks whose
 * `type` is actually a node type.  This prevents the ProseMirror
 * "There is no mark type X in this schema" RangeError that occurs
 * when AI-generated content puts block types inside `marks`.
 */
export function sanitizeTiptapDoc<T extends TiptapDocNode>(doc: T): T {
  if (!doc || typeof doc !== 'object') return doc;

  const cleaned: TiptapDocNode = { ...doc };

  if (cleaned.type === 'doc' && Array.isArray(cleaned.content)) {
    cleaned.content = sanitizeTiptapNodes(cleaned.content);
    return cleaned as T;
  }

  const [sanitized] = sanitizeSingleTiptapNode(cleaned);
  return (sanitized ?? cleaned) as T;
}
